import React, { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MessageCircle, CalendarHeart } from "lucide-react";
import { SITE, waLink } from "../lib/site";

const NAV = [
  { to: "/", label: "Inicio" },
  { to: "/quien-soy", label: "Quién Soy" },
  { to: "/control-prenatal", label: "Control Prenatal" },
  { to: "/salud-ginecologica", label: "Salud Ginecológica" },
  { to: SITE.bookingUrl, label: "Agendar Cita", external: true },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "py-2" : "py-4"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <div
          className={`flex items-center justify-between rounded-full transition-all duration-500 ${
            scrolled
              ? "glass-strong premium-shadow px-3 md:px-5 py-2"
              : "glass px-3 md:px-5 py-2.5"
          }`}
        >
          {/* Logo */}
          <Link
            to="/"
            data-testid="header-logo-link"
            className="flex items-center gap-3 pl-1 pr-2 group"
          >
            <div className="relative">
              <div className="absolute -inset-1 rounded-full bg-brand-bright/30 blur-md opacity-0 group-hover:opacity-100 transition-opacity" />
              <img
                src={SITE.logo}
                alt="Dr. Jairo Franco"
                className="relative w-10 h-10 md:w-11 md:h-11 rounded-full object-cover ring-1 ring-brand-silver/70"
              />
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif text-[15px] md:text-[17px] text-brand-deep tracking-tight">
                Dr. Jairo Franco
              </span>
              <span className="text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-brand-medical/80 font-medium">
                Ginecólogo · Obstetra
              </span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => {
              if (item.external) {
                return (
                  <a
                    key={item.to}
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="relative px-3 py-2 text-sm font-medium text-brand-deep/70 hover:text-brand-medical transition-colors duration-300"
                  >
                    {item.label}
                  </a>
                );
              }
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `relative px-3 py-2 text-sm font-medium transition-colors duration-300 ${
                      isActive
                        ? "text-brand-medical nav-active"
                        : "text-brand-deep/70 hover:text-brand-medical"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          {/* Right CTAs */}
          <div className="flex items-center gap-2">
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-btn"
              aria-label="WhatsApp"
              className="hidden sm:inline-flex items-center justify-center w-10 h-10 rounded-full text-brand-medical hover:text-white hover:bg-brand-medical transition-all duration-300 ring-1 ring-brand-silver/70"
            >
              <MessageCircle className="w-[18px] h-[18px]" strokeWidth={1.4} />
            </a>
            <a
              href={SITE.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-agendar-btn"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand-medical hover:bg-brand-deep text-white text-sm font-medium tracking-wide px-5 py-2.5 transition-all duration-300 premium-shadow"
            >
              <CalendarHeart className="w-4 h-4" strokeWidth={1.4} />
              Agendar cita
            </a>
            <button
              data-testid="mobile-menu-toggle"
              aria-label="Menú"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-full text-brand-deep hover:bg-brand-cloud transition-colors"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="lg:hidden mt-2 glass-strong premium-shadow rounded-3xl p-4"
              data-testid="mobile-menu"
            >
              <ul className="flex flex-col">
                {NAV.map((item) => {
                  if (item.external) {
                    return (
                      <li key={item.to}>
                        <a
                          href={item.to}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="block px-4 py-3 rounded-2xl text-base font-medium text-brand-deep hover:bg-brand-cloud/70 transition-colors"
                        >
                          {item.label}
                        </a>
                      </li>
                    );
                  }
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === "/"}
                        data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={({ isActive }) =>
                          `block px-4 py-3 rounded-2xl text-base font-medium transition-colors ${
                            isActive
                              ? "bg-brand-cloud text-brand-medical"
                              : "text-brand-deep hover:bg-brand-cloud/70"
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  );
                })}
                <li className="mt-2 grid grid-cols-2 gap-2">
                  <a
                    href={SITE.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-whatsapp-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full ring-1 ring-brand-silver text-brand-medical py-3 text-sm font-medium"
                  >
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                  <a
                    href={SITE.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="mobile-agendar-btn"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-medical text-white py-3 text-sm font-medium"
                  >
                    <CalendarHeart className="w-4 h-4" /> Agendar
                  </a>
                </li>
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
}
